import { isDefined } from "@clipboard-health/util-ts";

import { EMBEDDED_MODE_KEY } from "../EmbeddedMode/EmbeddedModeContext";

const LAST_RESTART_TIMESTAMP_KEY = "cbh_error_page_last_restart_timestamp";
const RESET_BUTTON_WINDOW_MS = 10 * 60 * 1000;

/**
 * Returns whether the "Reset App" button should be shown.
 * True when a restart was attempted within the last 10 minutes,
 * indicating the user is stuck in a recurring error loop.
 */
export function shouldShowResetButton(): boolean {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
  const timestamp = globalThis.sessionStorage.getItem(LAST_RESTART_TIMESTAMP_KEY);

  if (!isDefined(timestamp)) {
    return false;
  }

  const elapsed = Date.now() - Number(timestamp);

  return elapsed >= 0 && elapsed < RESET_BUTTON_WINDOW_MS;
}

/**
 * Records the current timestamp when the user taps "Restart App",
 * so that the next error page visit within 10 minutes can offer
 * the more aggressive "Reset App" option.
 */
export function markRestartAttempted(): void {
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
  globalThis.sessionStorage.setItem(LAST_RESTART_TIMESTAMP_KEY, String(Date.now()));
}

/**
 * Clears all client-side state (localStorage, sessionStorage, IndexedDB)
 * and forces a full page reload to recover from unrecoverable auth state.
 */
export async function resetApp(): Promise<void> {
  // Preserve embedded-mode context — this is runtime display context, not auth state,
  // and losing it breaks token restoration from the parent frame.
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
  const wasEmbedded = globalThis.sessionStorage.getItem(EMBEDDED_MODE_KEY) === "true";
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
  globalThis.localStorage.clear();
  // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
  globalThis.sessionStorage.clear();
  if (wasEmbedded) {
    // eslint-disable-next-line n/no-unsupported-features/node-builtins -- Browser API
    globalThis.sessionStorage.setItem(EMBEDDED_MODE_KEY, "true");
  }
  try {
    await clearIndexedDatabase();
  } catch {
    // Best-effort cleanup — don't block the redirect
  }
  globalThis.location.replace("/");
}

async function clearIndexedDatabase(): Promise<void> {
  if (!globalThis.indexedDB?.databases) {
    return;
  }

  const databases = await globalThis.indexedDB.databases();
  await Promise.all(
    databases.map(
      (database) =>
        new Promise<void>((resolve) => {
          if (!isDefined(database.name)) {
            resolve();
            return;
          }

          const request = globalThis.indexedDB.deleteDatabase(database.name);
          request.onsuccess = () => {
            resolve();
          };
          request.onerror = () => {
            resolve();
          };
          request.onblocked = () => {
            resolve();
          };
        })
    )
  );
}
